import {
  NormalizedCustomAttributeDefinition,
  NormalizedCustomAttributeGroup,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { getCustomAttributeConfig } from 'src/graphql/entities/custom-attributes/field-view-registry';
import {
  CustomAttributeRowData,
  CustomAttributeRowGroup,
} from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { resolveStoredValue } from 'src/graphql/entities/custom-attributes/payload';

export const UNGROUPED_ATTRIBUTE_GROUP_KEY = '__ungrouped';

interface GroupCustomAttributesParams {
  definitions: NormalizedCustomAttributeDefinition[];
  definedGroups: NormalizedCustomAttributeGroup[];
  parsedValues: Record<string, unknown>;
  savingKeys: Set<string>;
  errorByKey: Map<string, string>;
}

/**
 * Decodes each definition's stored value (falling back to its default) into
 * row data and buckets the rows by group: groups sorted by the defined-groups
 * index, then by label, with ungrouped rows always last.
 */
export function groupCustomAttributes({
  definitions,
  definedGroups,
  parsedValues,
  savingKeys,
  errorByKey,
}: GroupCustomAttributesParams): CustomAttributeRowGroup[] {
  const allRows: CustomAttributeRowData[] = definitions.map((definition) => {
    const field = getCustomAttributeConfig(definition.type);
    const rawValue = resolveStoredValue(parsedValues, definition.key, definition.defaultValue);
    const parsedValue = field.decode(rawValue);

    return {
      key: definition.key,
      definition,
      parsedValue,
      isEmpty: field.isEmpty(parsedValue),
      isSaving: savingKeys.has(definition.key),
      error: errorByKey.get(definition.key) ?? null,
    };
  });

  return bucketByGroup(allRows, indexGroups(definedGroups));
}

/** The same section order for a catalog alone (no values): grouped first by the defined order, ungrouped last. */
export function sortDefinitionsByGroup(
  definitions: NormalizedCustomAttributeDefinition[],
  definedGroups: NormalizedCustomAttributeGroup[],
): NormalizedCustomAttributeDefinition[] {
  const entries = definitions.map((definition) => ({ definition }));

  return bucketByGroup(entries, indexGroups(definedGroups)).flatMap((bucket) =>
    bucket.rows.map((row) => row.definition),
  );
}

function indexGroups(definedGroups: NormalizedCustomAttributeGroup[]): Map<string, number> {
  return new Map(definedGroups.map((group, index) => [group.key, index]));
}

interface GroupBucket<T> {
  groupKey: string;
  groupLabel: string;
  rows: T[];
}

function bucketByGroup<T extends { definition: NormalizedCustomAttributeDefinition }>(
  rows: T[],
  groupOrder: Map<string, number>,
): GroupBucket<T>[] {
  const buckets = new Map<string, GroupBucket<T>>();

  for (const row of rows) {
    const bucketKey = row.definition.groupKey ?? UNGROUPED_ATTRIBUTE_GROUP_KEY;
    let bucket = buckets.get(bucketKey);

    if (!bucket) {
      bucket = {
        groupKey: bucketKey,
        groupLabel: row.definition.groupLabel ?? '',
        rows: [],
      };

      buckets.set(bucketKey, bucket);
    }

    bucket.rows.push(row);
  }

  return [...buckets.values()].sort((a, b) => {
    const aIndex = groupOrder.get(a.groupKey) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = groupOrder.get(b.groupKey) ?? Number.MAX_SAFE_INTEGER;

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    if (a.groupKey === UNGROUPED_ATTRIBUTE_GROUP_KEY) {
      return 1;
    }

    if (b.groupKey === UNGROUPED_ATTRIBUTE_GROUP_KEY) {
      return -1;
    }

    return a.groupLabel.localeCompare(b.groupLabel);
  });
}
